import type { CSSProperties } from 'react';

/**
 * Ambient effects.
 *
 * The brief was "alive, not busy", so everything here is deliberately weak and
 * slow: a sheen that passes every twelve seconds or so, borders that breathe
 * over eight, arcs that ping over six. Each element takes a `delay` so several
 * on one screen never fire together, which is what would make it feel like a
 * loading state rather than atmosphere.
 *
 * The parent must be `position: relative` with `overflow: hidden` — every card
 * these are used in already is.
 */

/** A light sweep travelling across a card. */
export function Sheen({
  delay = 0,
  duration = 12,
  strength = 0.07,
}: {
  delay?: number;
  duration?: number;
  strength?: number;
}) {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: '55%',
        pointerEvents: 'none',
        background: `linear-gradient(100deg, transparent, rgba(var(--color-text-rgb), ${strength}) 45%, rgba(var(--color-accent-rgb), ${strength * 1.4}) 55%, transparent)`,
        animation: `sheen ${duration}s cubic-bezier(.4,0,.2,1) ${delay}s infinite`,
        // Promoted so the sweep does not repaint the card underneath it.
        willChange: 'transform, opacity',
      }}
    />
  );
}

/**
 * A ring from the Nocturne arc motif, slowly pinging outward.
 *
 * `mode` picks the behaviour: `ping` expands and fades, for decorative rings on
 * hero cards; `breathe` stays put and varies opacity, for rings sitting behind
 * text where movement would pull focus.
 */
export function PulseArc({
  size,
  top,
  right,
  opacity = 0.18,
  delay = 0,
  duration = 6,
  mode = 'ping',
}: {
  size: number;
  top: number;
  right: number;
  opacity?: number;
  delay?: number;
  duration?: number;
  mode?: 'ping' | 'breathe';
}) {
  const style: CSSProperties = {
    position: 'absolute',
    right,
    top,
    width: size,
    height: size,
    borderRadius: '50%',
    border: `1px solid rgba(var(--color-accent-rgb), ${opacity})`,
    pointerEvents: 'none',
    animation:
      mode === 'ping'
        ? `arcPulse ${duration}s cubic-bezier(.2,.6,.3,1) ${delay}s infinite`
        : `arcBreathe ${duration}s ease-in-out ${delay}s infinite`,
    willChange: 'transform, opacity',
  };
  return <div aria-hidden style={style} />;
}

/** Border that breathes toward the accent. Applied to quiet outlined cards. */
export function glowBorder(delay = 0, duration = 8): CSSProperties {
  return { animation: `borderGlow ${duration}s ease-in-out ${delay}s infinite` };
}
